import env from './env';
import { getPreviewVersion } from './previewOverride';

import type { RemoteFile } from '../constants/remote';

declare global {
  interface Window {
    env: Record<string, string>;
  }
}

export const isTargetServiceOnLocal = (serviceName: string) => {
  return serviceName === window?.env?.KC_DEV_REMOTE_TARGET_SERVICE;
};

export type EndpointReturn = {
  primary: string;
  standby?: string;
};

/**
 * 서비스이름과 로드할 파일명을 조합하여 endpoint를 구성합니다.
 *
 * @param serviceName 리모트 서비스 이름입니다.
 * @param serviceVersion 리모트 서비스의 버전입니다.
 * @param fileName 리모트 서비스 모듈 내 로드할 파일명입니다.
 * @returns 환경변수, serviceName, fileName에 의해 구성된 URL.
 */
export function getRemoteEndpoint(
  serviceName: string,
  serviceVersion: string,
  fileName: RemoteFile,
): EndpointReturn {
  const isProduction = process.env.NODE_ENV !== 'development';
  const previewVersion = getPreviewVersion(serviceName);
  const version = previewVersion || getOverrideVersion(serviceName) || serviceVersion;
  // 프리뷰(pr/{번호})는 덮어쓰기 배포라 고정 파일명인 remoteEntry.js가 캐시되면 PR 갱신이 반영되지 않으므로 프리뷰에서만 캐시버스터를 붙입니다.
  const cacheBuster = previewVersion ? `?t=${Date.now()}` : '';

  if (isProduction || !isTargetServiceOnLocal(serviceName)) {
    return {
      primary: `${window.env.KC_REMOTE_ENDPOINT_PRIMARY}/services/${serviceName}/${version}/static/chunks/${fileName}${cacheBuster}`,
      standby: `${window.env.KC_REMOTE_ENDPOINT_STANDBY}/services/${serviceName}/${version}/static/chunks/${fileName}${cacheBuster}`,
    };
  } else {
    return {
      primary: `${window.env.KC_DEV_REMOTE_URL}/_next/static/chunks/${fileName}`,
      standby: `${window.env.KC_DEV_REMOTE_URL}/_next/static/chunks/${fileName}`,
    };
  }
}

/**
 *
 * @param moduleId loadRemote 시 사용 (e.g. vpc/vpcs)
 * @returns vpc/vpcs
 */
export function getModuleName(moduleId?: string) {
  if (!moduleId) return;
  const firstSlashIndex = moduleId.indexOf('/');
  if (!firstSlashIndex) return;
  return moduleId.slice(0, firstSlashIndex);
}

export function getExposeKey(moduleId?: string) {
  if (!moduleId) return;
  const firstSlashIndex = moduleId.indexOf('/');
  if (!firstSlashIndex) return;
  return moduleId.slice(firstSlashIndex + 1);
}

/**
 * kc-helper를 사용해 서비스 버전을 오버라이드합니다.
 * @param serviceName 버전을 오버라이드할 서비스 이름입니다.
 * @returns 오버라이드된 버전 문자열. stage 환경이 아니거나 설정이 없으면 undefined.
 */
export function getOverrideVersion(serviceName: string) {
  if (env.KC_ENV === 'stage') {
    const localStorageServices = localStorage.getItem('kc-helper-services');

    if (!localStorageServices) return;

    const parsedServices: { name: string; version: string }[] = JSON.parse(localStorageServices);

    return parsedServices.find(service => service.name === serviceName)?.version;
  }
}
